import { BulkShelfResponse, BulkStockTrackingResponse, ColourCodePlanResponse, NextProductCodesResponse, PriceChangeList, VariantLabelInfoList, ProductAttributeList, ProductList, ProductResponse, ProductColourList, ProductColourPlanResponse, ProductColourResponse, ProductColoursResponse, } from '@aeris/contracts';
import { request } from "./client.js";
/**
 * `/api/v1/catalog` — produtos e variantes.
 *
 * Arquivar em vez de apagar: o papel da aplicação não tem DELETE em tabela de
 * catálogo (Master Specification seção 30), e um verbo que prometesse exclusão
 * descreveria algo que o produto deliberadamente não faz.
 */
/**
 * Built field by field rather than by iterating the object.
 *
 * Iterating gives `unknown` values that have to be stringified blindly, and an
 * object slipping in would serialise as "[object Object]" and silently return
 * the wrong page.
 */
export const searchParams = (query) => {
    const params = new URLSearchParams();
    if (query.search !== undefined && query.search.length > 0) {
        params.set('search', query.search);
    }
    if (query.searchMode !== undefined)
        params.set('searchMode', query.searchMode);
    if (query.status !== undefined)
        params.set('status', query.status);
    if (query.awaitingStock === true)
        params.set('awaitingStock', 'true');
    // F247: o arquivado até ter estoque, e o que falta no cadastro.
    if (query.shelved !== undefined)
        params.set('shelved', query.shelved);
    if (query.siteHidden !== undefined) {
        params.set('siteHidden', query.siteHidden);
    }
    if (query.missing !== undefined && query.missing.length > 0) {
        params.set('missing', query.missing.join(','));
    }
    /*
     * Os filtros que ficavam pelo caminho (F182).
     *
     * A tela mandava todos estes, o serializador não escrevia nenhum, e o
     * resultado era um chip de filtro ao lado de uma lista sem filtro. Ver
     * `ProductListRequest`, onde está por que o compilador não pegou.
     */
    if (query.brand !== undefined && query.brand.length > 0) {
        params.set('brand', query.brand);
    }
    if (query.categoryId !== undefined)
        params.set('categoryId', query.categoryId);
    if (query.condition !== undefined)
        params.set('condition', query.condition);
    if (query.priceMin !== undefined)
        params.set('priceMin', query.priceMin);
    if (query.priceMax !== undefined)
        params.set('priceMax', query.priceMax);
    if (query.stockMin !== undefined)
        params.set('stockMin', query.stockMin);
    if (query.stockMax !== undefined)
        params.set('stockMax', query.stockMax);
    if (query.applicationModelId !== undefined) {
        params.set('applicationModelId', query.applicationModelId);
    }
    if (query.page !== undefined)
        params.set('page', String(query.page));
    if (query.pageSize !== undefined) {
        params.set('pageSize', String(query.pageSize));
    }
    if (query.sort !== undefined)
        params.set('sort', query.sort);
    if (query.direction !== undefined)
        params.set('direction', query.direction);
    // Comma-separated, and omitted entirely when empty: an `include=` with
    // nothing after it would ask the server to parse a single empty name.
    if (query.include !== undefined && query.include.length > 0) {
        params.set('include', query.include.join(','));
    }
    if (query.priceListId !== undefined) {
        params.set('priceListId', query.priceListId);
    }
    if (query.branchId !== undefined)
        params.set('branchId', query.branchId);
    const encoded = params.toString();
    return encoded.length > 0 ? `?${encoded}` : '';
};
export const fetchProducts = async (query, signal) => ProductList.parse(await request(`/api/v1/catalog/products${searchParams(query)}`, { signal }));
/** Um produto, como está agora no servidor — a versão nova, por exemplo (F248). */
export const fetchProduct = async (id, signal) => ProductResponse.parse(await request(`/api/v1/catalog/products/${id}`, { signal }));
/** O código e o código de barras que o Novo produto mostra ao abrir (F133). */
export const fetchNextProductCodes = async (signal) => NextProductCodesResponse.parse(await request('/api/v1/catalog/products/next-codes', { signal }));
export const createProduct = async (input) => ProductResponse.parse(await request('/api/v1/catalog/products', { method: 'POST', body: input }));
export const updateProduct = async (id, input) => ProductResponse.parse(await request(`/api/v1/catalog/products/${id}`, {
    method: 'PATCH',
    body: input,
}));
/**
 * Liga ou desliga o controle de estoque de todo um conjunto (F070).
 *
 * Manda o filtro e as exceções, nunca a lista de ids — ver
 * `BulkStockTrackingRequest`, onde está o porquê.
 */
export const setProductsStockTracking = async (input) => BulkStockTrackingResponse.parse(await request('/api/v1/catalog/products/stock-tracking', {
    method: 'POST',
    body: input,
}));
/**
 * Arquivar até ter estoque (F247): o conjunto do filtro, menos quem tem saldo
 * em alguma filial — `kept` diz quantos ficaram por isso.
 */
export const shelveProducts = async (input) => BulkShelfResponse.parse(await request('/api/v1/catalog/products/shelve', {
    method: 'POST',
    body: input,
}));
/** Ocultar da loja online (F291), com estoque ou sem. */
export const hideProductsFromStorefront = async (input) => BulkShelfResponse.parse(await request('/api/v1/catalog/products/hide-from-storefront', {
    method: 'POST',
    body: input,
}));
/** "Mostrar no site" (F291). */
export const showProductsOnStorefront = async (input) => BulkShelfResponse.parse(await request('/api/v1/catalog/products/show-on-storefront', {
    method: 'POST',
    body: input,
}));
/** "Mostrar de novo" (F247): tira a marca do conjunto. */
export const unshelveProducts = async (input) => BulkShelfResponse.parse(await request('/api/v1/catalog/products/unshelve', {
    method: 'POST',
    body: input,
}));
export const archiveProduct = async (id, version) => ProductResponse.parse(await request(`/api/v1/catalog/products/${id}/archive`, {
    method: 'POST',
    body: { version },
}));
/**
 * "Liberar sem contar" (F133): o produto que aguardava a conferência do
 * estoque volta a vender sem movimento nenhum.
 */
export const releaseProductStockHold = async (id, version) => ProductResponse.parse(await request(`/api/v1/catalog/products/${id}/release-stock-hold`, {
    method: 'POST',
    body: { version },
}));
export const restoreProduct = async (id, version) => ProductResponse.parse(await request(`/api/v1/catalog/products/${id}/restore`, {
    method: 'POST',
    body: { version },
}));
/**
 * A ficha técnica de um produto (F108).
 *
 * Rota própria, ao contrário de peso e embalagem, que viajam no cadastro: é
 * uma lista, tem widget próprio dentro do diálogo e grava na hora — como as
 * aplicações e as fotos. Ver `SetProductAttributesRequest` para por que ela
 * não carrega `version`.
 */
export const fetchProductAttributes = async (productId, signal) => ProductAttributeList.parse(await request(`/api/v1/catalog/products/${productId}/attributes`, {
    signal,
}));
/**
 * O histórico de preço e custo de um produto (F125) — as 100 mudanças mais
 * recentes da variante padrão, com quem, quando e a margem de então.
 */
export const fetchPriceHistory = async (productId, signal) => PriceChangeList.parse(await request(`/api/v1/catalog/products/${productId}/price-history`, {
    signal,
}));
/**
 * Nome, SKU, GTIN e preço de várias variantes, numa requisição (F124).
 * É o que a fila de etiquetas precisa e a linha de compra não traz.
 */
export const fetchVariantLabelInfo = async (variantIds, signal) => VariantLabelInfoList.parse(await request(`/api/v1/catalog/variants/label-info?ids=${encodeURIComponent(variantIds.join(','))}`, { signal }));
/**
 * O mesmo, a partir do que o LEITOR devolve (F184).
 *
 * Código de barras ou código do produto. Sob `catalog.read`, e não sob o
 * `/pos/lookup` do PDV, que exige `sales.write`: quem só etiqueta não vende.
 */
export const fetchLabelInfoByCodes = async (codes, signal) => VariantLabelInfoList.parse(await request(`/api/v1/catalog/variants/label-info?codes=${encodeURIComponent(codes.join(','))}`, { signal }));
/**
 * O mesmo, a partir de produtos — o que a seleção do catálogo conhece (F124).
 * Cada produto volta pela sua variante padrão.
 */
export const fetchProductLabelInfo = async (productIds, signal) => VariantLabelInfoList.parse(await request(`/api/v1/catalog/variants/label-info?productIds=${encodeURIComponent(productIds.join(','))}`, { signal }));
export const setProductAttributes = async (productId, input) => ProductAttributeList.parse(await request(`/api/v1/catalog/products/${productId}/attributes`, {
    method: 'PUT',
    body: input,
}));
// --- As cores do produto (F176) ----------------------------------------------
export const fetchProductColours = async (productId, signal) => ProductColourList.parse(await request(`/api/v1/catalog/products/${productId}/variants`, { signal }));
export const createProductColour = async (productId, input) => ProductColourResponse.parse(await request(`/api/v1/catalog/products/${productId}/variants`, {
    method: 'POST',
    body: input,
}));
/**
 * O que o lote de cores vai gerar, sem gravar nada (F191).
 *
 * POST numa leitura porque a lista de nomes é o corpo — seis cores numa query
 * string é a URL que um proxy corta no meio. Como `fetchColourCodePlan`, exige
 * `catalog.write`: é a prévia de uma escrita.
 */
export const planProductColours = async (productId, input, signal) => ProductColourPlanResponse.parse(await request(`/api/v1/catalog/products/${productId}/variants/batch/plan`, {
    method: 'POST',
    body: input,
    signal,
}));
/** Cria várias cores numa transação só — tudo ou nada (F191). */
export const createProductColours = async (productId, input) => ProductColoursResponse.parse(await request(`/api/v1/catalog/products/${productId}/variants/batch`, {
    method: 'POST',
    body: input,
}));
export const updateProductColour = async (productId, variantId, input) => ProductColourResponse.parse(await request(`/api/v1/catalog/products/${productId}/variants/${variantId}`, { method: 'PATCH', body: input }));
export const setProductColourStatus = async (productId, variantId, action, version) => ProductColourResponse.parse(await request(`/api/v1/catalog/products/${productId}/variants/${variantId}/${action}`, { method: 'POST', body: { version } }));
// --- O acerto de códigos das cores (F181) ------------------------------------
/**
 * O que o acerto mudaria, sem mudar nada.
 *
 * Lê com `catalog.write` de propósito: é a prévia de uma escrita, e quem não
 * pode gravar não precisa da lista do que gravaria.
 */
export const fetchColourCodePlan = async (signal) => ColourCodePlanResponse.parse(await request('/api/v1/catalog/colour-codes', { signal }));
/**
 * Grava o acerto, e devolve o que aconteceu de verdade.
 *
 * Com `body: {}` embora não haja nada a mandar: um POST sem corpo sai sem
 * `content-type`, e é o tipo de detalhe que passa nos testes de tela (que
 * dublam esta função) e falha na loja.
 */
export const applyColourCodes = async () => ColourCodePlanResponse.parse(await request('/api/v1/catalog/colour-codes', { method: 'POST', body: {} }));
//# sourceMappingURL=catalog.js.map